import { i18n } from './i18n.js';
import { MoonfallAnalytics } from './analytics/posthog-init.js';
import { MoonfallReferralLink } from './referral-link.js';
import { obtainSignupChallengeToken } from './turnstile-challenge.js';
/**
 * Convex Client for Clerk Integration
 *
 * Wraps the ConvexHttpClient for use with Clerk authentication.
 * Uses Clerk JWT tokens instead of custom tokens.
 */

// Will be initialized after config is loaded
let convexClient = null;
let convexDeploymentUrl = null;
let authToken = null;
const assetUrlCache = new Map();
const pendingAssetRequests = new Map();
const activeAssetRequestControllers = new Set();
const ASSET_URL_CACHE_LIMIT = 256;
const ASSET_URL_REQUEST_TIMEOUT_MS = 8_000;
const activeReceiptControllers = new Set();
const activePackControllers = new Set();
const STELLAR_RECEIPT_TIMEOUT_MS = 12_000;
const STELLAR_RECEIPT_MUTATIONS = new Set([
    'stellar:claimStellarFirstBanner',
    'stellar:acknowledgeStellarFirstBanner',
    'stellar:releaseStellarFirstBanner',
]);

/**
 * MoonfallCloud - Client-side Convex API wrapper
 */
/* The sign-up check's refusals are the player's to act on (retry, or turn off
   a blocker), so they are told in the player's language rather than in the
   server's English sentence (launch audit round 3, 2026-09-26). */
const SIGNUP_CHALLENGE_REFUSALS = {
    signup_challenge_required: ['signupChallenge.failed', "The sign-up check didn't pass. Please try again."],
    signup_challenge_failed: ['signupChallenge.failed', "The sign-up check didn't pass. Please try again."],
    signup_challenge_unavailable: ['signupChallenge.timedOut', 'The sign-up check took too long. Please try again.'],
};

function signupChallengeRefusalText(code) {
    const entry = SIGNUP_CHALLENGE_REFUSALS[code];
    if (!entry) return null;
    const [key, fallback] = entry;
    const translated = i18n?.t?.(key);
    return translated && translated !== key ? translated : fallback;
}

const MoonfallCloud = (function () {

    function indexMediaEntries(entries, idField) {
        const output = {};
        if (!Array.isArray(entries)) return output;
        entries.forEach((entry) => {
            if (!entry || typeof entry !== 'object') return;
            const id = entry[idField];
            if (typeof id !== 'string' || !id) return;
            output[id] = typeof entry.url === 'string' ? entry.url : null;
        });
        return output;
    }

    function currentLanguage() {
        return i18n?.getLanguage?.() || 'en';
    }

    function sanitizeMediaUrl(value) {
        if (typeof value !== 'string') return null;
        const trimmed = value.trim();
        if (!trimmed) return null;
        try {
            let pathname = new URL(trimmed, window.location.href).pathname;
            for (let pass = 0; pass < 3; pass += 1) {
                const decoded = decodeURIComponent(pathname);
                if (decoded === pathname) break;
                pathname = decoded;
            }
            if (pathname === '/assets/art_placeholder.webp') {
                return null;
            }
        } catch (_) {
            return null;
        }
        if (trimmed.startsWith('http') || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
            return trimmed;
        }
        if (trimmed.startsWith('/') || trimmed.startsWith('/assets/') || trimmed.startsWith('assets/')) {
            return trimmed;
        }
        return null;
    }

    function readCachedAssetUrl(assetId) {
        if (!assetUrlCache.has(assetId)) return null;
        const url = assetUrlCache.get(assetId);
        assetUrlCache.delete(assetId);
        assetUrlCache.set(assetId, url);
        return url;
    }

    function cacheAssetUrl(assetId, url) {
        if (!url) {
            assetUrlCache.delete(assetId);
            return;
        }
        assetUrlCache.delete(assetId);
        assetUrlCache.set(assetId, url);
        while (assetUrlCache.size > ASSET_URL_CACHE_LIMIT) {
            assetUrlCache.delete(assetUrlCache.keys().next().value);
        }
    }

    function abortAssetRequests() {
        activeAssetRequestControllers.forEach((controller) => controller.abort());
        activeAssetRequestControllers.clear();
        pendingAssetRequests.clear();
    }

    /* A PLAYER'S LISTS ARE READ WHOLE, PAGE BY PAGE (card-scale, 21 October
       2026). The single-call reads stopped at 4,000 rows without saying so, or
       failed outright on a large collection. Each page is bounded by the
       server; a reader follows the cursor to the end, and an answer that is
       malformed or never ends fails loudly instead of returning part of the
       list. */
    const MAX_LIBRARY_PAGES = 2000;
    // The most of a list read whole into the page: the game server's own
    // bound for whole lists (COLLECTION_LIST_MAX_ENTRIES). Past it the reader
    // stops (`too_many_items`) and the screen pages it from the server.
    const WHOLE_LIST_MAX_ITEMS = 5000;

    async function readEveryPage(name, args = {}, { maxItems = Infinity } = {}) {
        if (!convexClient) throw new Error('Convex client not initialized');
        const items = [];
        let cursor = null;
        for (let pages = 0; pages < MAX_LIBRARY_PAGES; pages += 1) {
            const page = await convexClient.query(name, cursor ? { ...args, cursor } : args);
            if (!page || !Array.isArray(page.items)) throw new Error(`${name}: page_invalid`);
            items.push(...page.items);
            if (items.length > maxItems) {
                throw Object.assign(new Error(`${name}: too_many_items`), { code: 'too_many_items' });
            }
            if (page.isDone) return items;
            if (typeof page.nextCursor !== 'string' || !page.nextCursor) throw new Error(`${name}: page_invalid`);
            cursor = page.nextCursor;
        }
        throw new Error(`${name}: page_limit`);
    }

    // The order moonExchange:listSellableCards answered in: by name, normal first.
    function sortSellableCards(cards) {
        return cards.sort((a, b) => {
            const nameCompare = String(a.baseName || '').localeCompare(String(b.baseName || ''));
            if (nameCompare !== 0) return nameCompare;
            if (a.variant === b.variant) return 0;
            return a.variant === 'normal' ? -1 : 1;
        });
    }

    function normalizeAssetIds(assetIds) {
        const seen = new Set();
        return (assetIds || [])
            .map((value) => typeof value === 'string' ? value.trim() : '')
            .filter((value) => value && !seen.has(value) && seen.add(value));
    }

    async function fetchMissingAssetUrls(assetIds) {
        if (!convexClient) throw new Error('Convex client not initialized');
        const ids = normalizeAssetIds(assetIds);
        if (ids.length === 0) return {};

        const controller = new AbortController();
        let timedOut = false;
        const timeoutId = setTimeout(() => {
            timedOut = true;
            controller.abort();
        }, ASSET_URL_REQUEST_TIMEOUT_MS);
        timeoutId.unref?.();
        activeAssetRequestControllers.add(controller);

        const requestToken = authToken;
        const requestClient = new window.ConvexHttpClient(convexDeploymentUrl, {
            logger: false,
            fetch: (input, init = {}) => window.fetch(input, {
                ...init,
                signal: controller.signal,
            }),
        });
        if (requestToken) requestClient.setAuth(requestToken);

        // A query failure is not proof that an asset does not exist. Preserve
        // that distinction so renderers can retry instead of caching a false
        // authoritative null for the rest of the session.
        let cardEntries;
        let characterEntries;
        try {
            [cardEntries, characterEntries] = await Promise.all([
                requestClient.query('cards:getCardImageUrls', { cardIds: ids }),
                requestClient.query('characters:getCharacterPortraitUrls', { characterIds: ids }),
            ]);
        } catch (cause) {
            if (timedOut) {
                const error = new Error('Card art resolver timed out');
                error.cause = cause;
                throw error;
            }
            if (controller.signal.aborted && requestToken !== authToken) {
                const error = new Error('Card art resolver authentication changed');
                error.cause = cause;
                throw error;
            }
            throw cause;
        } finally {
            clearTimeout(timeoutId);
            // Promise.all rejects on the first failed resolver. Abort the
            // shared transport before releasing ownership so its sibling
            // cannot survive indefinitely outside the auth/init lifecycle.
            if (!controller.signal.aborted) controller.abort();
            activeAssetRequestControllers.delete(controller);
        }

        const cardMap = indexMediaEntries(cardEntries, 'cardId');
        const characterMap = indexMediaEntries(characterEntries, 'characterId');
        const output = {};

        ids.forEach((id) => {
            output[id] = sanitizeMediaUrl(characterMap[id]) || sanitizeMediaUrl(cardMap[id]) || null;
        });

        return output;
    }

    /* ONE MOONLIGHT READ, SHARED FOR A MOMENT.

       A signed-in boot asked for the same single row three times: the chip
       mounts and reads, the Store's lobby warm reads ~200 ms after that
       answer, and the lobby's Lucid check reads beside the Store's (read
       audit R27). A caller now joins a read already out, and reuses its
       answer for five seconds from the moment it was asked. From the ASK,
       for a read still out too: a lost request stops collecting callers
       after five seconds rather than holding every later one with it.

       IT NEVER SERVES ANOTHER ACCOUNT'S ANSWER AND NEVER HIDES A CHANGE. The
       answer is dropped on every signal that the balance moved, on an
       identity change, on a new client, and after this client's own writes
       that can move it. An answer belongs to the moment it was asked: when
       any of those lands while it is on its way — or between a caller's ask
       and its delivery (store.js reads, THEN announces stale) — that caller
       is asked again rather than handed a figure that may predate it. Only a
       known answer is kept: 'unknown' is the auth race or an account not
       made yet, and the next caller asks again. */
    const MOONLIGHT_STATUS_REUSE_MS = 5_000;
    /* Bounded: signals landing faster than a round trip cannot keep a caller
       asking. Past the bound it takes the last answer, and only if that is
       still this account's. */
    const MOONLIGHT_STATUS_MAX_ASKS = 4;
    const MOONLIGHT_MOVED_EVENTS = Object.freeze([
        'moonfall:moonlight-stale',      // a checkout return (store.js)
        'moonfall:moonlight-changed',    // a grant: a referral, a voucher
        'moonfall:subscription-changed', // a plan: the tier, the cycle, the Refill offer
    ]);
    /* This client's writes that can move the balance, by either door (the
       typed API or the raw `convex.mutation`). A new account's welcome grant
       and a voucher claim are handled where they are made. A match spends
       Moonlight on the game server, out of this client's sight: a surface
       that knows one started or ended calls `users.forgetMoonlightStatus()`. */
    const MOONLIGHT_MOVING_MUTATIONS = new Set(['referrals:debugRecordReferralFinish']);
    let moonlightEpoch = 0;
    let moonlightRead = null;

    function forgetMoonlightStatus() {
        moonlightEpoch += 1;
        moonlightRead = null;
    }

    /** Still the question the signed-in account would ask now. */
    function isCurrentMoonlightRead(read) {
        return read.epoch === moonlightEpoch && read.client === convexClient && read.token === authToken;
    }

    function askMoonlightStatus() {
        const read = { epoch: moonlightEpoch, client: convexClient, token: authToken, askedAt: Date.now(), promise: null };
        read.promise = Promise.resolve(convexClient.query('usageCaps:getMoonlightStatus', {})).then(
            (answer) => {
                if (moonlightRead === read && answer?.state !== 'known') moonlightRead = null;
                return answer;
            },
            (error) => {
                if (moonlightRead === read) moonlightRead = null;
                throw error;
            },
        );
        moonlightRead = read;
        return read;
    }

    async function readMoonlightStatus() {
        for (let ask = 1; ; ask += 1) {
            if (!convexClient) throw new Error('Convex client not initialized');
            const shared = moonlightRead;
            const read = shared && isCurrentMoonlightRead(shared)
                && Date.now() - shared.askedAt < MOONLIGHT_STATUS_REUSE_MS
                ? shared
                : askMoonlightStatus();
            const answer = await read.promise;
            if (!isCurrentMoonlightRead(read) && ask < MOONLIGHT_STATUS_MAX_ASKS) continue;
            if (read.token !== authToken) throw new Error('Moonlight status was read for another account');
            // Each caller its own copy (the status is flat), as when each asked alone.
            return answer && typeof answer === 'object' ? { ...answer } : answer;
        }
    }

    /** A write that can move the balance. Dropped whether or not its reply
     *  arrives: a lost reply may still have committed. */
    async function mutateMoonlight(name, args) {
        try {
            return await convexClient.mutation(name, args);
        } finally {
            forgetMoonlightStatus();
        }
    }

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        MOONLIGHT_MOVED_EVENTS.forEach((name) => window.addEventListener(name, forgetMoonlightStatus));
    }

    /**
     * Initialize the Convex client
     * @param {string} convexUrl - The Convex deployment URL
     */
    function init(convexUrl) {
        if (!convexUrl) {
            console.error('[MoonfallCloud] No CONVEX_URL provided');
            return false;
        }

        // ConvexHttpClient will be loaded via convex-loader.js
        if (typeof window.ConvexHttpClient === 'undefined') {
            console.error('[MoonfallCloud] ConvexHttpClient not loaded');
            return false;
        }

        convexClient?.clearAuth?.();
        abortAssetRequests();
        activePackControllers.forEach(controller => controller.abort());
        activePackControllers.clear();
        convexDeploymentUrl = convexUrl;
        convexClient = new window.ConvexHttpClient(convexDeploymentUrl);
        assetUrlCache.clear();
        pendingAssetRequests.clear();
        forgetMoonlightStatus();
        if (authToken) {
            convexClient.setAuth(authToken);
            announceAuthIdentity();
        }
        console.log('[MoonfallCloud] Initialized with URL:', convexUrl);
        return true;
    }

    /**
     * ANNOUNCE THE IDENTITY, so identity-scoped reads can be retaken.
     *
     * A Convex query issued before the token is attached is anonymous, and the
     * server answers it truthfully rather than failing — `getMoonlightStatus`
     * returns `{ state: 'unknown' }`. A one-shot reader that lost that race had
     * no way to learn the identity had since arrived, so it painted "unknown"
     * forever on a full account. This is the signal it retries on, and it
     * covers the refresh case too: a new token is a new identity to re-read at.
     */
    function announceAuthIdentity() {
        if (typeof window === 'undefined' || typeof window.CustomEvent !== 'function') return;
        try {
            window.dispatchEvent(new window.CustomEvent('moonfall:convex-auth-changed', {
                detail: { authenticated: Boolean(authToken) },
            }));
        } catch (err) {
            console.warn('[MoonfallCloud] Could not announce auth identity:', err);
        }
    }

    /**
     * Set the auth token (Clerk JWT)
     */
    function setAuthToken(token) {
        const nextToken = typeof token === 'string' && token.trim() ? token.trim() : null;
        const changed = nextToken !== authToken;
        if (changed) {
            activeReceiptControllers.forEach((controller) => controller.abort());
            activeReceiptControllers.clear();
            abortAssetRequests();
            activePackControllers.forEach(controller => controller.abort());
            activePackControllers.clear();
            // Another identity's Moonlight is never this one's.
            forgetMoonlightStatus();
            // Nor is what the consent check said about another identity.
            if (tokenClaims(nextToken)?.sub !== knownNewSubject) knownNewSubject = null;
        }
        authToken = nextToken;
        if (changed) announceAuthIdentity();
        if (!convexClient) return;
        if (changed && convexDeploymentUrl) {
            // Convex queues mutations per client and reads `client.auth` only
            // when each queued call begins. Never install a new identity on an
            // old queue: clear that queue's credential and give the new session
            // a fresh client. In-flight work keeps the token it already sent;
            // queued work on the retired client can no longer become user B.
            convexClient.clearAuth?.();
            convexClient = new window.ConvexHttpClient(convexDeploymentUrl);
        }
        if (authToken) {
            convexClient.setAuth(authToken);
        } else {
            convexClient.clearAuth?.();
        }
    }

    /**
     * Get the current auth token
     */
    function getAuthToken() {
        return authToken;
    }

    /**
     * Check if client is ready
     */
    function isReady() {
        return convexClient !== null;
    }

    // ============================================================
    // USER API
    // ============================================================

    // Refusals that mean "fetch a signup ticket and try again". These are read
    // from ConvexError.data, never from error.message: a production Convex
    // deployment redacts every error message to a bare "Server Error", so a
    // message-sniffing check silently disables signup in production only.
    const NEEDS_SIGNUP_TICKET = new Set(['signup_ticket_required', 'signup_ticket_invalid']);

    /* A NEW ACCOUNT IS GIVEN ITS WELCOME MOONLIGHT, so a read taken before it
       existed ('unknown') is not an answer for it. An existing sign-in
       (`isNewUser: false`) moves nothing and keeps the boot's shared read. */
    function noteAccountSync(result) {
        if (result?.isNewUser !== false) forgetMoonlightStatus();
        return result;
    }

    /** The ID token's claims, read WITHOUT verifying: they only choose the
     *  order of the requests below. Every check that matters is the server's,
     *  on the verified token. */
    function tokenClaims(token) {
        const payload = String(token || '').split('.')[1];
        if (!payload) return null;
        try {
            const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
            const claims = JSON.parse(atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4)));
            return claims && typeof claims === 'object' ? claims : null;
        } catch (_) {
            return null;
        }
    }

    /* THE CONSENT CHECK ALREADY KNOWS WHO IS NEW.

       Every new account's signup used to open with a write certain to fail
       (`users:getOrCreateUser` without a ticket, refused with
       `signup_ticket_required`: 0.3-0.7 s on the snappiness meter's round 15,
       1.5 s on a cold WebKit phone) before it fetched its ticket and wrote
       again. `gdpr:checkConsentVersion`, asked a moment earlier by the consent
       gate with the same identity, answers `isNewUser` for an identity with no
       account. Such an identity fetches its ticket FIRST, from the same
       endpoint with the same bearer token; the server checks it exactly as
       before, and the one write carries it.

       Only for a VERIFIED email: an unverified identity is refused
       (`email_verification_required`) BEFORE any ticket, so that it spends no
       signup admission, and it keeps the plain write first. Without a ticket
       the plain write decides, exactly as when it went first. The answer is
       kept per identity (the token's subject), so a refreshed token keeps it
       and another account never inherits it. */
    const ACCOUNT_STATUS_QUERY = 'gdpr:checkConsentVersion';
    let knownNewSubject = null;

    function noteAccountStatus(status, askedWithToken) {
        const subject = tokenClaims(askedWithToken)?.sub;
        if (typeof subject !== 'string' || !subject) return;
        // An answer for an identity no longer signed in says nothing.
        if (tokenClaims(authToken)?.sub !== subject) return;
        if (status?.authenticated !== true) return;
        if (status.isNewUser === true) knownNewSubject = subject;
        else if (knownNewSubject === subject) knownNewSubject = null;
    }

    function isKnownNewVerifiedIdentity() {
        const claims = tokenClaims(authToken);
        return Boolean(knownNewSubject
            && claims?.sub === knownNewSubject
            && claims.email_verified === true
            && typeof claims.email === 'string' && claims.email);
    }

    /** A signup ticket from the game server, or the failure shaped like a Convex refusal. */
    async function requestSignupTicket() {
        if (!authToken) {
            const failure = new Error('Authentication is required to create an account.');
            failure.code = 'signup_auth_required';
            failure.data = { code: failure.code, message: failure.message };
            throw failure;
        }
        // The bot challenge the server asks for when Turnstile is
        // configured (null when it is not). A failure to run it is the
        // player's to retry, and says so.
        const turnstileToken = await obtainSignupChallengeToken();
        const response = await fetch('/api/auth/signup-ticket', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(turnstileToken ? { turnstileToken } : {}),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.ticket) {
            const failure = new Error(signupChallengeRefusalText(payload?.code)
                || payload?.error
                || 'Signup is temporarily unavailable.');
            failure.code = payload?.code || 'signup_ticket_unavailable';
            // Mirror the Convex refusal shape so callers have exactly one
            // place to read a server-authored, user-safe reason from.
            failure.data = { code: failure.code, message: failure.message };
            if (payload?.retryAfterMs) failure.retryAfterMs = payload.retryAfterMs;
            throw failure;
        }
        return payload.ticket;
    }

    function finishAccountSync(result, signupArgs) {
        // The account exists now: the next sync of this identity is a sign-in.
        if (knownNewSubject && tokenClaims(authToken)?.sub === knownNewSubject) knownNewSubject = null;
        return resolveReferralSignupResult(noteAccountSync(result), signupArgs.referralCode);
    }

    /** A known new, verified identity: the ticket first, then one write with it. */
    async function createKnownNewAccount(signupArgs) {
        let ticket = null;
        let ticketFailure = null;
        try {
            ticket = await requestSignupTicket();
        } catch (failure) {
            ticketFailure = failure;
        }
        if (!ticket) {
            // Without a ticket the plain write decides, as when it went first:
            // an account made meanwhile (another tab) signs in, and a new one
            // reports the ticket's refusal, as the retry path did.
            try {
                return finishAccountSync(await convexClient.mutation('users:getOrCreateUser', signupArgs), signupArgs);
            } catch (error) {
                if (NEEDS_SIGNUP_TICKET.has(error?.data?.code)) throw ticketFailure;
                throw error;
            }
        }
        return finishAccountSync(await convexClient.mutation('users:getOrCreateUser', {
            ...signupArgs,
            signupTicket: ticket,
        }), signupArgs);
    }

    function resolveReferralSignupResult(result, referralCode) {
        if (!referralCode) return result;
        if (!result?.referralStatus) {
            /* An EXISTING account signed in with a stale invite code still in
               session storage. Only a first signup can ever consume the code,
               so for this account it is spent air — and iOS restores
               sessionStorage with the tab indefinitely, which is how one
               visitor's code kept resurrecting a "saved you a seat" ticket
               for whoever signed in next (the velvetvale97 phantom). */
            MoonfallReferralLink?.clearPendingCode?.(referralCode);
            return result;
        }
        MoonfallReferralLink?.clearPendingCode?.(referralCode);
        if (result.referralStatus.linked === true) {
            MoonfallAnalytics?.capture?.('referral_signup_linked');
        }
        return result;
    }

    const users = {
        /**
         * Get or create user from the Firebase identity. Creating a NEW
         * account requires a short-lived signup ticket from the game server
         * (its IP-aware anti-abuse chokepoint); existing sign-ins return
         * without one, so the extra round-trip happens only on first signup.
         * An identity the consent check already named new fetches its ticket
         * first (see `createKnownNewAccount`).
         */
        async getOrCreate() {
            if (!convexClient) throw new Error('Convex client not initialized');
            const referralCode = MoonfallReferralLink?.getPendingCode?.();
            const signupArgs = typeof referralCode === 'string' && /^[0-9a-f]{32}$/.test(referralCode)
                ? { referralCode }
                : {};
            if (isKnownNewVerifiedIdentity()) return await createKnownNewAccount(signupArgs);
            try {
                return finishAccountSync(await convexClient.mutation('users:getOrCreateUser', signupArgs), signupArgs);
            } catch (error) {
                if (!NEEDS_SIGNUP_TICKET.has(error?.data?.code)) {
                    throw error;
                }
            }
            const ticket = await requestSignupTicket();
            return finishAccountSync(await convexClient.mutation('users:getOrCreateUser', {
                ...signupArgs,
                signupTicket: ticket,
            }), signupArgs);
        },

        /**
         * Get user profile
         */
        async getProfile() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('users:getProfile', {});
        },

        /**
         * Update username
         */
        async updateProfile(username) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('users:updateProfile', { username });
        },

        /**
         * Choose the lobby avatar: a summoner's characterId, or null to clear.
         * The server checks the summoner is one this account may show.
         */
        async setAvatar(characterId) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('users:setAvatar', { characterId: characterId ?? null });
        },

        /**
         * Initialize card collection for new user
         */
        async initializeCards(cardIds) {
            if (!convexClient) throw new Error('Convex client not initialized');
            const args = Array.isArray(cardIds) ? { cardIds } : {};
            return await convexClient.mutation('users:initializeUserCards', args);
        },

        /**
         * Get all owned cards
         */
        async getCards() {
            return await readEveryPage('users:getUserCardsPage');
        },

        /**
         * Check if user has paid subscription
         */
        async isPaid() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('users:isPaidUser', {});
        },

        /** The player's own Moonlight, for the chrome meter. Self-scoped.
         *  One read shared for a moment (see `readMoonlightStatus`). */
        async getMoonlightStatus() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await readMoonlightStatus();
        },

        /** Drop the shared Moonlight answer: something this client cannot
         *  see moved it (a match started or ended on the game server). */
        forgetMoonlightStatus() {
            forgetMoonlightStatus();
        },

        async getMangaGameStatus() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('usageCaps:getMangaGameStatus', {});
        },

        /**
         * Consume newly unlocked cardbacks that haven't been acknowledged yet
         */
        async consumeNewCardbackUnlocks() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('users:consumeNewCardbackUnlocks', {});
        },

        /**
         * Consume newly earned first-discovery achievements that haven't been acknowledged yet
         */
        async consumeNewFirstDiscoveryAchievements() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('users:consumeNewFirstDiscoveryAchievements', {});
        },

        async consumeNewRecipeDiscoveryAchievements() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('users:consumeNewRecipeDiscoveryAchievements', {});
        },

        async consumeStarterRewardAnnouncement() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('users:consumeStarterRewardAnnouncement', {});
        },
    };

    // ============================================================
    // ATTRIBUTION API (first-party marketing attribution)
    // ============================================================

    const attribution = {
        /**
         * Persist captured first/last touch. The server is authoritative and
         * silently refuses unless marketing consent has been granted.
         */
        async record(firstTouch, lastTouch) {
            if (!convexClient) throw new Error('Convex client not initialized');
            const args = {};
            if (firstTouch) args.firstTouch = firstTouch;
            if (lastTouch) args.lastTouch = lastTouch;
            return await convexClient.mutation('attribution:recordAttribution', args);
        },
        /** The current user's attribution + marketing-consent state. */
        async mine() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('attribution:getMyAttribution', {});
        },
    };

    // ============================================================
    // REFERRAL API
    // ============================================================

    const referrals = {
        async getOrCreateCode() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('referrals:getOrCreateMyReferralCode', {});
        },
        async getProgram() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('referrals:getMyReferralProgram', {});
        },
        async consumeRewardAnnouncements() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('referrals:consumeMyReferralRewardAnnouncements', {});
        },
    };

    // ============================================================
    // DISCORD PINGS API (convex/discordPings.ts)
    // ============================================================

    const discordPings = {
        /** { linked: false } or { linked, username, sales, moonDream, dmsClosed }. */
        async mine() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('discordPings:myDiscordPings', {});
        },
        async set(kinds) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('discordPings:setDiscordPings', kinds);
        },
        async unlink() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('discordPings:unlinkDiscord', {});
        },
    };

    // ============================================================
    // VOUCHER API
    // ============================================================

    const vouchers = {
        async previewRedemption(code) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('vouchers:previewRedemption', { code: String(code || '') });
        },
        /* The claim goes through the game server: a gift claimed on a paid
           plan defers billing in Stripe, which only the server can do. The
           server runs the same identity-authenticated mutation and answers in
           its shape — a refusal is a VALUE (`{ redeemed: false, code }`); a
           ban or a lost session throws with `data.code`, like a ConvexError. */
        /**
         * expectedResetAt is the preview's reviewResetAt: the source usage
         * cycle boundary, not the new gift's first reset or a chosen anchor.
         * @param {string} code @param {{expectedResetAt?: number}} options
         */
        async redeem(code, { expectedResetAt } = {}) {
            const refusal = (failureCode, message) => {
                const failure = new Error(message || 'Could not redeem this code.');
                failure.code = failureCode;
                failure.data = { code: failureCode, message: failure.message };
                return failure;
            };
            if (!authToken) throw refusal('authentication_required', 'Sign in to redeem a code.');
            try {
                const response = await fetch('/api/vouchers/redeem', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                    body: JSON.stringify({ code: String(code || ''), ...(expectedResetAt === undefined ? {} : { expectedResetAt }) }),
                });
                const payload = await response.json().catch(() => null);
                if (!response.ok) throw refusal(payload?.code || 'voucher_unavailable', payload?.error);
                return payload;
            } finally {
                // A claim that went out may have granted Moonlight or a pass,
                // even when its reply is lost.
                forgetMoonlightStatus();
            }
        },
    };

    // ============================================================
    // DECK API
    // ============================================================

    function assertMutablePersonalDeckId(deckId) {
        if (String(deckId || '').startsWith('starter:')) {
            throw new Error('Official starter decks are read-only');
        }
    }

    /* A deck made, changed or deleted: whoever keeps deck lists ahead (the
       summoner select reads them whole at lobby idle) drops them. Announced
       once the mutation settles, answered or not — a reply lost after the
       server committed the change is still a change. */
    async function announcingDeckChange(action, run) {
        try {
            return await run();
        } finally {
            try {
                window.dispatchEvent(new CustomEvent('moonfall:decks-changed', { detail: { action } }));
            } catch (_) { /* no window to tell */ }
        }
    }

    const decks = {
        /**
         * Create a new deck
         */
        async create(name, character, cardIds) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await announcingDeckChange('create', () => convexClient.mutation('decks:createDeck', { name, character, cardIds }));
        },

        /**
         * Update an existing deck
         */
        async update(deckId, name, character, cardIds) {
            if (!convexClient) throw new Error('Convex client not initialized');
            assertMutablePersonalDeckId(deckId);
            return await announcingDeckChange('update', () => convexClient.mutation('decks:updateDeck', {
                deckId,
                name: name || undefined,
                character: character || undefined,
                cardIds: cardIds || undefined,
            }));
        },

        /**
         * Delete a deck
         */
        async delete(deckId) {
            if (!convexClient) throw new Error('Convex client not initialized');
            assertMutablePersonalDeckId(deckId);
            return await announcingDeckChange('delete', () => convexClient.mutation('decks:deleteDeck', { deckId }));
        },

        /**
         * List all user decks
         */
        async list() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('decks:listDecks', {});
        },

        /**
         * List immutable, code-owned starter templates for read-only surfaces.
         */
        async listOfficial() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('decks:listOfficialStarterDecks', {});
        },

        /**
         * List personal decks plus immutable official starter templates.
         */
        async listPlayable(characterKeys, cursor = null, numItems = 25) {
            if (!convexClient) throw new Error('Convex client not initialized');
            const args = { characterKeys, numItems };
            if (cursor) args.cursor = cursor;
            return await convexClient.query('decks:listPlayableDecks', args);
        },

        /**
         * Get a specific deck
         */
        async get(deckId) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('decks:getDeck', { deckId });
        },

        /**
         * Resolve a personal deck or official starter template for match entry.
         */
        async getPlayable(deckId) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('decks:getPlayableDeck', { deckId });
        },

    };

    // ============================================================
    // SUBSCRIPTION API
    // ============================================================

    const subscriptions = {
        /**
         * Get subscription status
         */
        async getStatus() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('subscriptions:getSubscriptionStatus', {});
        },

        /**
         * Paid stardust purchases must go through the Store Stripe checkout flow.
         */
        async addStardust() {
            throw new Error('Use the Store checkout flow for stardust purchases.');
        },

        /**
         * Paid subscriptions must go through the Store Stripe checkout flow.
         */
        async subscribe() {
            throw new Error('Use the Store checkout flow for subscriptions.');
        },

        /**
         * Get subscription history
         */
        async getHistory() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('subscriptions:getSubscriptionHistory', {});
        },
    };

    // ============================================================
    // CARD LIBRARY API (Game content from Convex)
    // ============================================================

    const cards = {
        /**
         * Get a single card by ID
         */
        async get(cardId) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('cards:getCard', { cardId, language: currentLanguage() });
        },

        /**
         * Get image URL for a card
         */
        async getImageUrl(cardId) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('cards:getCardImageUrl', { cardId });
        },

        /**
         * Get image URLs for multiple cards (batch)
         */
        async getImageUrls(cardIds) {
            if (!convexClient) throw new Error('Convex client not initialized');
            const entries = await convexClient.query('cards:getCardImageUrls', { cardIds });
            return indexMediaEntries(entries, 'cardId');
        },
    };

    // ============================================================
    // CHARACTERS API
    // ============================================================

    const characters = {
        /**
         * List all playable characters
         */
        async list() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('characters:listCharacters', { language: currentLanguage() });
        },

        /**
         * Get a character by ID
         */
        async get(characterId) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('characters:getCharacter', { characterId, language: currentLanguage() });
        },

        /**
         * Get character portrait URL
         */
        async getPortraitUrl(characterId) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('characters:getCharacterPortraitUrl', { characterId });
        },

        /**
         * Get portrait URLs for multiple characters (batch)
         */
        async getPortraitUrls(characterIds) {
            if (!convexClient) throw new Error('Convex client not initialized');
            const entries = await convexClient.query('characters:getCharacterPortraitUrls', { characterIds });
            return indexMediaEntries(entries, 'characterId');
        },
    };

    const media = {
        peekUrl(assetId) {
            const normalized = typeof assetId === 'string' ? assetId.trim() : '';
            if (!normalized) return null;
            return readCachedAssetUrl(normalized);
        },

        async getUrls(assetIds) {
            const ids = normalizeAssetIds(assetIds);
            const output = {};
            const missing = [];

            ids.forEach((id) => {
                if (assetUrlCache.has(id)) {
                    output[id] = readCachedAssetUrl(id);
                    return;
                }
                missing.push(id);
            });

            if (missing.length > 0) {
                const promises = [];
                const idsToFetch = [];

                missing.forEach((id) => {
                    if (pendingAssetRequests.has(id)) {
                        promises.push(pendingAssetRequests.get(id));
                        return;
                    }
                    idsToFetch.push(id);
                });

                if (idsToFetch.length > 0) {
                    let batchPromise;
                    batchPromise = fetchMissingAssetUrls(idsToFetch)
                        .then((resolved) => {
                            idsToFetch.forEach((id) => {
                                cacheAssetUrl(id, resolved[id]);
                                if (pendingAssetRequests.get(id) === batchPromise) {
                                    pendingAssetRequests.delete(id);
                                }
                            });
                            return resolved;
                        })
                        .catch((err) => {
                            idsToFetch.forEach((id) => {
                                if (pendingAssetRequests.get(id) === batchPromise) {
                                    pendingAssetRequests.delete(id);
                                }
                            });
                            throw err;
                        });

                    idsToFetch.forEach((id) => pendingAssetRequests.set(id, batchPromise));
                    promises.push(batchPromise);
                }

                if (promises.length > 0) {
                    await Promise.all(promises);
                }

                ids.forEach((id) => {
                    output[id] = readCachedAssetUrl(id);
                });
            }

            return output;
        },

        async getUrl(assetId) {
            const normalized = typeof assetId === 'string' ? assetId.trim() : '';
            if (!normalized) return null;
            const results = await this.getUrls([normalized]);
            return results[normalized] || null;
        },
    };

    // ============================================================
    // BATTLE LOCATIONS API
    // ============================================================

    const locations = {
        /**
         * List all battle locations
         */
        async list() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('locations:listLocations', { language: currentLanguage() });
        },

        /**
         * Get a random location
         */
        async getRandom() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('locations:getRandomLocation', { language: currentLanguage() });
        },

        /**
         * Get a location by ID
         */
        async get(locationId) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('locations:getLocation', { locationId, language: currentLanguage() });
        },
    };

    // ============================================================
    // SOUND ASSETS API
    // ============================================================

    const sounds = {
        /**
         * Get a sound URL by soundId
         */
        async getUrl(soundId) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('sounds:getSoundUrl', { soundId });
        },

        /**
         * Get multiple sound URLs by soundId
         */
        async getUrls(soundIds) {
            if (!convexClient) throw new Error('Convex client not initialized');
            const entries = await convexClient.query('sounds:getSoundUrls', { soundIds });
            return indexMediaEntries(entries, 'soundId');
        },

        /**
         * List sounds by category
         */
        async listByCategory(category) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('sounds:listSoundsByCategory', { category });
        },
    };

    // ============================================================
    // CARDBACKS API
    // ============================================================

    /**
     * A cardback's name, description and unlock hint in the player's language.
     * The catalog row holds the English; the localized copy lives with the rest
     * of the UI text under `cardbacks.<slug>` in ui.json, so every surface that
     * lists the catalog (Collection, unlock banners, the Foundry) reads it
     * localized through this one place. A slug without an entry keeps its
     * catalog text rather than showing a key.
     */
    function localizeCardback(cardback) {
        if (!cardback || typeof cardback.slug !== 'string') return cardback;
        const localized = { ...cardback };
        for (const field of ['name', 'description', 'unlockHint']) {
            const key = `cardbacks.${cardback.slug}.${field}`;
            const text = i18n?.t?.(key);
            if (typeof text === 'string' && text && text !== key) localized[field] = text;
        }
        return localized;
    }

    const cardbacks = {
        /**
         * List all cardbacks, localized for the current language.
         */
        async list() {
            if (!convexClient) throw new Error('Convex client not initialized');
            const rows = await convexClient.query('cardbacks:list', {});
            return Array.isArray(rows) ? rows.map(localizeCardback) : rows;
        },

        /**
         * Re-localize one catalog row for the language selected NOW — for a
         * surface that keeps the list across a language switch and re-renders.
         */
        localize: localizeCardback,

        /**
         * Select a cardback
         */
        async select(slug) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('cardbacks:select', { slug });
        },

        /**
         * Get storage URL for a cardback image
         */
    };

    // ============================================================
    // CARD PACKS API
    // ============================================================

    // A timed-out confirmation may still have committed. The session keeps its
    // original request ID; a dedicated transport prevents it blocking unrelated
    // mutations and captures this identity for the entire request.
    async function packRequest(method, name, args, { signal = undefined } = {}) {
        if (!convexClient) throw new Error('Convex client not initialized');
        const controller = new AbortController();
        const abort = () => controller.abort();
        if (signal?.aborted) abort();
        signal?.addEventListener('abort', abort, { once: true });
        activePackControllers.add(controller);
        const token = authToken, deployment = convexDeploymentUrl;
        const timeout = setTimeout(abort, 12000);
        try {
            if (controller.signal.aborted) throw new DOMException('Request cancelled', 'AbortError');
            const client = method === 'route' ? null : new window.ConvexHttpClient(deployment, {
                auth: token || undefined,
                logger: false,
                fetch: (input, init = {}) => window.fetch(input, { ...init, signal: controller.signal }),
            });
            const result = method === 'route'
                ? await postPackRoute(name, args, token, controller.signal)
                : method === 'mutation'
                    ? await client.mutation(name, args, { skipQueue: true })
                    : await client.query(name, args);
            if (controller.signal.aborted || token !== authToken || deployment !== convexDeploymentUrl)
                throw new DOMException('Request cancelled', 'AbortError');
            return result;
        } finally {
            clearTimeout(timeout);
            signal?.removeEventListener('abort', abort);
            activePackControllers.delete(controller);
        }
    }

    /* Buying, opening and exchanging for a pack go through the Moonfall
       server, which alone sees the visitor's country (packs are off in
       Brazil). A refusal keeps Convex's shape: its `data` carries the code
       playerFacingError reads, and its message the code the exchange session
       matches. */
    async function postPackRoute(path, body, token, signal = undefined) {
        if (!token) throw new Error('Not authenticated');
        const response = await window.fetch(path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(body),
            ...(signal ? { signal } : {}),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
            const data = payload && typeof payload === 'object' ? payload : {};
            const message = typeof data.code === 'string' ? data.code
                : response.status === 401 ? 'Not authenticated' : `Request failed (${response.status})`;
            throw Object.assign(new Error(message), { data });
        }
        return payload;
    }

    const cardPacks = {
        createExchangeSessionApi() {
            const token = authToken, deployment = convexDeploymentUrl;
            const scoped = (name, args) => {
                if (token !== authToken || deployment !== convexDeploymentUrl)
                    return Promise.reject(new Error('duplicate_session_auth_changed'));
                return cardPacks[name](...args);
            };
            return {
                ensureTotals: (...args) => scoped('ensureTotals', args),
                getExchange: (...args) => scoped('getExchange', args),
                startExchange: (...args) => scoped('startExchange', args),
            };
        },
        async getScreenState(options = {}) {
            const config = await cardPacks.getConfig(options);
            if (config?.openingCardVersion !== undefined
                && (config.openingCardVersion !== 1 || config.duplicateExchangeVersion !== 1))
                throw new Error('pack_state_invalid');
            if (config?.duplicateExchangeVersion === undefined) {
                const state = await cardPacks.getPackState(options);
                return { config, state, exchangeVersion: 0 };
            }
            const state = config.packState;
            if (config.duplicateExchangeVersion !== 1 || state?.schemaVersion !== 1
                || !(state.accountId === null || (typeof state.accountId === 'string' && state.accountId))
                || !Number.isSafeInteger(state.packCount) || state.packCount < 0
                || !(state.activePack === null || (typeof state.activePack?.id === 'string'
                    && Array.isArray(state.activePack.items) && state.activePack.items.length <= 5)))
                throw new Error('pack_state_invalid');
            return { config, state, exchangeVersion: 1 };
        },
        ensureTotals(options = {}) {
            return packRequest('mutation', 'cardPacks:ensureDuplicateTotals', {}, options);
        },
        getExchange(operationId, options = {}) {
            return packRequest('query', 'cardPacks:getDuplicateExchange', operationId ? { operationId } : {}, options);
        },
        getOpeningCard(request, options = {}) {
            return packRequest('query', 'cardPacks:getOpeningCard', request, options);
        },
        startExchange(request, options = {}) {
            return packRequest('route', '/api/card-packs/exchange', request, options);
        },
        /**
         * Get pack configuration (price, size, stellar odds)
         */
        async getConfig(options = {}) {
            return await packRequest('query', 'cardPacks:getConfig', {}, options);
        },

        /**
         * Get pack inventory and any active opening
         */
        async getPackState(options = {}) {
            return await packRequest('query', 'cardPacks:getPackState', {}, options);
        },

        /**
         * Development only: point THIS account's Stellar rate at a testable
         * number so a pull can be exercised without opening hundreds of packs.
         * Pass nothing to clear it. Convex refuses this on any deployment but
         * development, so it is inert in production.
         */
        async setDebugStellarOdds(oddsPercent) {
            if (!convexClient) throw new Error('Convex client not initialized');
            const args = Number.isFinite(oddsPercent) ? { oddsPercent } : {};
            return await convexClient.mutation('cardPacks:setDebugStellarOdds', args);
        },

        /**
         * Buy card packs
         */
        async buyPack(quantity = 1) {
            const args = {};
            if (Number.isFinite(quantity)) {
                args.quantity = quantity;
            }
            return await postPackRoute('/api/card-packs/buy', args, authToken);
        },

        /**
         * Record that the player tore a charged pack open (survives reloads).
         */
        async markPackOpened(packId) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('cardPacks:markPackOpened', { packId });
        },

        /**
         * Open a card pack
         */
        async openPack() {
            return await postPackRoute('/api/card-packs/open', {}, authToken);
        },

        /**
         * Claim a single card from a pack opening
         */
        async claimPackCard(packId, index) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('cardPacks:claimPackCard', { packId, index });
        },

        /**
         * Convert non-craftable duplicates into packs
         */
        async convertNonCraftableDuplicates() {
            return await postPackRoute('/api/card-packs/convert', {}, authToken);
        },
    };

    const stardustDenomination = {
        async getConfig() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('stardustDenomination:getPublicConfig', {});
        },
    };

    // ============================================================
    // QUESTS API
    // ============================================================

    const quests = {
        /**
         * Sync quests (expire, assign daily, return active + recent completions)
         */
        async sync() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('quests:sync', { language: currentLanguage() });
        },

        /**
         * List active quests without assignment side effects
         */
        /** @param {{ language?: string }} [options] */
        async listActive({ language } = {}) {
            if (!convexClient) throw new Error('Convex client not initialized');
            // `language`: a switch reads the rail in its new language before applying it.
            return await convexClient.query('quests:listActive', { language: language || currentLanguage() });
        },

        /**
         * Debug: simulate a real match win through the authoritative progress
         * rule, evaluated against the caller's real saved decks (dev mode only).
         * A quest only advances if its card is in one of your decks.
         */
        async debugSimulateWin() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('quests:debugSimulateWin', {});
        },

        /**
         * Debug: force-complete every active quest, bypassing deck/card rules,
         * only to exercise the completion banner + reward path (dev mode only).
         */
        async debugForceComplete() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('quests:debugForceComplete', {});
        },

        /**
         * Debug: assign a new quest (dev mode only)
         */
        async debugAssignQuest(count = 1) {
            if (!convexClient) throw new Error('Convex client not initialized');
            const args = {};
            if (Number.isFinite(count)) {
                args.count = count;
            }
            return await convexClient.mutation('quests:debugAssignQuest', args);
        },

        /**
         * Debug: bank one WIN toward the Moon Exchange unlock (dev mode only).
         *
         * Walks the same recorder a finished, won match does, so ten presses
         * leave an account indistinguishable from one that earned it. Returns
         * the gate's progress so the caller can render it without a second read.
         */
        async debugRecordExchangeWin() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('referrals:debugRecordExchangeWin', {});
        },

        /**
         * Debug: bank one FINISHED match toward the referral (dev mode only).
         *
         * The referral counts finishes rather than wins, and settles 3 card
         * packs plus Moonlight to both sides on the third — this drives the real
         * settlement, so the packs, the Moonlight and the announcement all land
         * through their normal paths.
         */
        async debugRecordReferralFinish() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await mutateMoonlight('referrals:debugRecordReferralFinish', {});
        },
        /**
         * Put a PENDING referral back on this account so the invite quest row
         * reappears. Once a referral qualifies the row is gone for good, and the
         * only other way back to it was deleting and recreating the account.
         */
        async debugCreatePendingReferral() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('referrals:debugCreatePendingReferral', {});
        },
    };

    // ============================================================
    // MOON EXCHANGE API
    // ============================================================

    const moonExchange = {
        /**
         * Get exchange config (fee rate, limits)
         */
        async getConfig() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('moonExchange:getConfig', {});
        },

        /**
         * Ensure search text backfill runs for legacy listings
         */
        async ensureSearchTextBackfill() {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('moonExchange:ensureSearchTextBackfill', {});
        },

        /**
         * List sellable inventory for the current user
         */
        async listSellableCards() {
            return sortSellableCards(await readEveryPage('moonExchange:listSellableCardsPage', {}, { maxItems: WHOLE_LIST_MAX_ITEMS }));
        },

        /**
         * List open listings
         */
        async listOpenListings(options = {}) {
            if (!convexClient) throw new Error('Convex client not initialized');
            const args = {};
            if (typeof options.cursor === 'string' && options.cursor.trim()) {
                args.cursor = options.cursor;
            }
            if (Number.isFinite(options.limit)) {
                args.limit = options.limit;
            }
            if (typeof options.sort === 'string' && options.sort) {
                args.sort = options.sort;
            }
            if (typeof options.search === 'string' && options.search.trim()) {
                args.search = options.search.trim();
            }
            if (options.mineOnly === true) {
                args.mineOnly = true;
            }
            if (options.excludeMine === true) {
                args.excludeMine = true;
            }
            return await convexClient.query('moonExchange:listOpenListings', args);
        },

        /**
         * One listing, as THIS signed-in viewer sees it.
         *
         * Only the facts that depend on who is asking -- whether it is theirs,
         * the price and how many they can actually buy. The card itself comes
         * from the public projection, which is the same for every reader, so a
         * shared link renders one screen rather than two that drift.
         */
        async listingForViewer(listingId) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.query('moonExchange:listingForViewer', { listingId });
        },

        /**
         * Create a sell listing
         */
        async createListing(cardId, unitPrice, quantity, isStellar = false) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('moonExchange:createListing', {
                cardId,
                unitPrice,
                quantity,
                isStellar: isStellar === true
            });
        },

        /**
         * Buy from a listing
         */
        async buyFromListing(listingId, quantity) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('moonExchange:buyFromListing', { listingId, quantity });
        },

        /**
         * Cancel a listing
         */
        async cancelListing(listingId) {
            if (!convexClient) throw new Error('Convex client not initialized');
            return await convexClient.mutation('moonExchange:cancelListing', { listingId });
        },
    };

    // ============================================================
    // RAW CONVEX ACCESS (for advanced use cases)
    // ============================================================

    const convex = {
        async query(name, args = {}) {
            if (!convexClient) throw new Error('Convex client not initialized');
            if (name !== ACCOUNT_STATUS_QUERY) return await convexClient.query(name, args);
            const askedWithToken = authToken;
            const status = await convexClient.query(name, args);
            noteAccountStatus(status, askedWithToken);
            return status;
        },
        async mutation(name, args = {}) {
            if (!convexClient) throw new Error('Convex client not initialized');
            if (MOONLIGHT_MOVING_MUTATIONS.has(name)) return await mutateMoonlight(name, args);
            return await convexClient.mutation(name, args);
        },
        async receiptMutation(name, args = {}) {
            if (!STELLAR_RECEIPT_MUTATIONS.has(name)) {
                const error = new Error('Unsupported Stellar receipt mutation.');
                error.code = 'stellar_transport_forbidden';
                throw error;
            }
            const deploymentUrl = convexClient?.url || window.CONVEX_URL || '';
            if (!deploymentUrl || typeof window.ConvexHttpClient !== 'function') {
                throw new Error('Convex client not initialized');
            }
            const token = authToken;
            if (!token) {
                const error = new Error('Stellar receipt delivery requires authentication.');
                error.code = 'stellar_transport_unauthenticated';
                throw error;
            }

            const controller = new AbortController();
            let timedOut = false;
            activeReceiptControllers.add(controller);
            const timeoutId = setTimeout(() => {
                timedOut = true;
                controller.abort();
            }, STELLAR_RECEIPT_TIMEOUT_MS);
            timeoutId.unref?.();

            try {
                // Receipt delivery must never enter the app-wide serialized
                // mutation queue: a lost mobile request would otherwise freeze
                // every later cloud write. A one-shot authenticated client plus
                // an aborting fetch gives this idempotent protocol a real bound.
                const receiptClient = new window.ConvexHttpClient(deploymentUrl, {
                    auth: token,
                    logger: false,
                    fetch: (input, init = {}) => window.fetch(input, {
                        ...init,
                        signal: controller.signal,
                    }),
                });
                return await receiptClient.mutation(name, args, { skipQueue: true });
            } catch (cause) {
                if (timedOut) {
                    const error = new Error('Stellar receipt transport timed out.');
                    error.code = 'stellar_transport_timeout';
                    error.cause = cause;
                    throw error;
                }
                if (controller.signal.aborted && token !== authToken) {
                    const error = new Error('Stellar receipt authentication changed.');
                    error.code = 'stellar_transport_auth_changed';
                    error.cause = cause;
                    throw error;
                }
                throw cause;
            } finally {
                clearTimeout(timeoutId);
                activeReceiptControllers.delete(controller);
            }
        },
    };

    // Public API
    // Top-level query/mutation shortcuts
    async function query(name, args) {
        return convex.query(name, args);
    }

    async function mutation(name, args) {
        return convex.mutation(name, args);
    }

    return {
        init,
        setAuthToken,
        getAuthToken,
        isReady,
        deploymentUrl: () => (convexClient && convexClient.url) || window.CONVEX_URL || '',
        query,
        mutation,
        users,
        attribution,
        referrals,
        discordPings,
        vouchers,
        decks,
        subscriptions,
        cards,
        characters,
        locations,
        sounds,
        cardbacks,
        cardPacks,
        stardustDenomination,
        quests,
        moonExchange,
        media,
        convex,
    };
})();

export { MoonfallCloud };
